import { useState, useLayoutEffect, useEffect } from 'react'

import { VideoBlobPaginatedList, VideosVideoIdBlobsBlobIdDeleteRequest } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery, useInfiniteQuery, useMutationState, InfiniteData, useQueryClient } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'

import { videoObjectBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs'
import { videoObjectQueryKeys, workspaceQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { mergeBatchIntoSource } from 'utils'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ virtualItems, setLoadingRows }: UseAsyncContentCaching) => {
  const queryClient = useQueryClient()

  const { workspaceId, videoobjectId } = videoObjectBlobsOverviewRoute.useParams()

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const videoBlobsQuery = useInfiniteQuery({
    ...videoObjectQueryKeys.blobList({
      videoId: videoobjectId,
    }),
    enabled: workspaceQuery.isSuccess,
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [batchGetEnabled, setBatchGetEnabled] = useState(videoBlobsQuery.isSuccess)

  const pendingVideoBlobs = useMutationState({
    filters: { mutationKey: ['content', 'video-blob', 'DELETE'], status: 'pending' },
    select: (mutation) => mutation.state.variables as VideosVideoIdBlobsBlobIdDeleteRequest,
  })

  const videoBlobBatchQuery = useQuery({
    ...videoObjectQueryKeys.blobBatchGet(
      { videoId: videoobjectId },
      () => virtualItems?.map((virtualItem) => videoBlobsQuery.data?.flat[virtualItem.index]?.id) as string[],
    ),
    enabled: batchGetEnabled,
    refetchInterval: RefetchIntervals.NORMAL,
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useLayoutEffect(() => {
    if (videoBlobsQuery.isSuccess && videoBlobBatchQuery.isSuccess) {
      queryClient.setQueriesData<InfiniteData<VideoBlobPaginatedList>>(
        { queryKey: videoObjectQueryKeys.blobLists() },
        (old) => {
          if (old) {
            return {
              ...old,
              pages:
                old.pages.map((page) => ({
                  ...page,
                  items: mergeBatchIntoSource(
                    page.items,
                    videoBlobBatchQuery.data.items,
                    new Set(videoBlobBatchQuery.data.notFoundIds),
                  ),
                })) ?? [],
            }
          }
          return old
        },
      )
    }
  }, [
    videoBlobBatchQuery.data?.items,
    videoBlobBatchQuery.data?.notFoundIds,
    videoBlobBatchQuery.isSuccess,
    videoBlobsQuery.isSuccess,
    queryClient,
  ])

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingVideoBlobs.map((task) => task.blobId)])])
  }, [pendingVideoBlobs, setLoadingRows])

  useEffect(() => {
    if (videoBlobsQuery.isSuccess) {
      if (!batchGetEnabled) {
        const timeoutHandle = setTimeout(() => {
          setBatchGetEnabled(true)
          clearTimeout(timeoutHandle)
        }, 5000)
      }
    }
  }, [batchGetEnabled, videoBlobsQuery.isSuccess])

  return {
    workspaceQuery,
    videoBlobsQuery,
    setBatchGetEnabled,
  }
}
